import { setSelectedWalletGTMEvent } from '@functions/analytics';
import { removeLocalStorageWithKeyStartingBy } from '@functions/localStorage';
import { captureException } from '@sentry/nextjs';
import { useMutation } from '@tanstack/react-query';
import { _connectEVMWallet } from '../features/evmStore/evmStore.functions';
import { IEVMProvider } from '../features/evmStore/evmStore.types';
import useStarknetWallets from '../features/starknetStore/hooks/useStarknetWallets';
import { _connectStarknetWallet } from '../features/starknetStore/starknetStore.functions';
import { IStarknetProvider } from '../features/starknetStore/starknetStore.type';
import { EWalletType } from '../web3Store.enums';
import { TWallets, TWeb3Account } from '../web3Store.type';
import useAddWeb3Account from './useAddWeb3Account';

export default function useConnectWallet() {
	const { addWeb3Account } = useAddWeb3Account();
	const [starknetWallets] = useStarknetWallets();

	const { mutate: connectWallet, ...mutationProps } = useMutation<
		| {
				provider: IEVMProvider;
				error?: any;
		  }
		| {
				provider: IStarknetProvider;
				error?: any;
		  },
		Error,
		TWallets
	>({
		mutationFn: (newWallet: TWallets) => {
			switch (newWallet.type) {
				case EWalletType.ethereum: {
					return _connectEVMWallet(newWallet);
				}
				case EWalletType.starknet: {
					if (starknetWallets.state !== 'hasData') {
						throw new Error('Wallet list not loaded');
					}

					const selectedWallet = starknetWallets.data?.find(
						(wallet) => wallet.id === newWallet.id && wallet.isInstalled
					);

					if (!selectedWallet || !selectedWallet.wallet) {
						throw new Error('Wallet not found');
					}

					try {
						// remove all item that contains gsw-last- in the key in local storage
						// clean up starknet wallet local storage data
						removeLocalStorageWithKeyStartingBy('gsw-last-');
					} catch (err) {}

					// send GTM event
					setSelectedWalletGTMEvent(EWalletType.starknet, selectedWallet.name);

					return _connectStarknetWallet(selectedWallet.wallet);
				}
				default:
					throw new Error('Invalid wallet type');
			}
		},
		onSuccess: async ({ provider, error }, newWallet) => {
			switch (newWallet.type) {
				case EWalletType.ethereum: {
					const typedProvider = provider as IEVMProvider;
					const network = await typedProvider?.web3Provider?.getNetwork();

					const newWeb3Account: TWeb3Account = {
						id: newWallet.id,
						name: newWallet.name,
						type: newWallet.type,
						provider,
						chainId: network?.chainId || -1
					};

					addWeb3Account(newWeb3Account);
					break;
				}
				case EWalletType.starknet: {
					const typedProvider = provider as IStarknetProvider;

					// sanity await to ensure the chainId is set in the provider object
					await typedProvider.web3Provider.getChainId();

					const newWeb3Account: TWeb3Account = {
						id: newWallet.id,
						name: newWallet.name,
						type: newWallet.type,
						provider,
						chainId: typedProvider.web3Provider?.chainId || -1
					};

					addWeb3Account(newWeb3Account);
					break;
				}
				default:
					break;
			}
		},
		onError: (error, newWallet) => {
			console.error('connectWallet', error);
			switch (newWallet.type) {
				case EWalletType.ethereum: {
					if (error.message !== 'Invalid network') captureException(error);

					break;
				}
				case EWalletType.starknet: {
					error.message !== 'No wallet selected' && captureException(error);
					break;
				}
			}
		}
	});

	return { connectWallet, ...mutationProps };
}
